import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../state/cart.store';
import { useToast } from './Toast';
import type { Product } from '../types';
import { getProducts } from '../lib/api';

const Cart: React.FC = () => {
    const { items, removeItem, clearCart, updateItemQuantity } = useCart();
    const toast = useToast();
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const data = await getProducts();
                if (!cancelled) setProducts(data);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const total = useMemo(() => {
        return items.reduce((sum, item) => {
            const product = products.find(p => p.id === item.productId);
            return sum + (product?.price ?? 0) * item.quantity;
        }, 0);
    }, [items, products]);

    const getProductTitle = (productId: number): string => {
        const product = products.find(p => p.id === productId);
        if (!product) return `Prodotto ${productId}`;
        return product.variantLabel
            ? `${product.name} (${product.variantLabel})`
            : product.name;
    };

    const handleRemove = (productId: number) => {
        const title = getProductTitle(productId);
        removeItem(productId);
        toast.show(`${title} rimosso dal carrello`, 'info');
    };

    const handleClear = () => {
        clearCart();
        toast.show('Carrello svuotato', 'info');
    };

    const handleQtyChange = (productId: number, delta: number) => {
        const item = items.find(i => i.productId === productId);
        if (!item) return;
        const newQty = item.quantity + delta;
        if (newQty <= 0) {
            handleRemove(productId);
        } else {
            updateItemQuantity(productId, newQty);
        }
    };

    return (
        <div className="cart">
            <h2>Carrello</h2>
            <p>
                <Link to="/">← Continua lo shopping</Link>
            </p>

            {items.length === 0 && !loading && (
                <div className="cart-empty">
                    <p>Il carrello è vuoto.</p>
                    <Link className="checkout-button" to="/">Sfoglia il menu</Link>
                </div>
            )}

            {items.length > 0 && (
                <>
                    <div className="cart-items">
                        {items.map(item => {
                            const product = products.find(p => p.id === item.productId);
                            const title = getProductTitle(item.productId);
                            const price = product?.price ?? 0;
                            const rowTotal = price * item.quantity;
                            return (
                                <div key={item.productId} className="cart-item-row">
                                    <div className="cart-item-info">
                                        <strong>{title}</strong>
                                        <span className="muted">€{price.toFixed(2)} cad.</span>
                                    </div>
                                    <div className="cart-item-controls">
                                        <div className="qty-row qty-row-small">
                                            <button type="button" onClick={() => handleQtyChange(item.productId, -1)}>
                                                −
                                            </button>
                                            <span className="qty-display">{item.quantity}</span>
                                            <button type="button" onClick={() => handleQtyChange(item.productId, 1)}>
                                                +
                                            </button>
                                        </div>
                                        <span className="cart-item-total">€{rowTotal.toFixed(2)}</span>
                                        <button
                                            className="remove-button"
                                            onClick={() => handleRemove(item.productId)}
                                            aria-label={`Rimuovi ${title}`}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="cart-total">
                        <strong>Totale:</strong> <strong>€{total.toFixed(2)}</strong>
                    </div>
                    <div className="actions">
                        <Link className="checkout-button" to="/checkout">
                            Vai al checkout
                        </Link>
                        <button className="clear-cart-button" onClick={handleClear}>
                            Svuota carrello
                        </button>
                    </div>
                </>
            )}

            {loading && products.length === 0 ? <p>Caricamento prezzi...</p> : null}
        </div>
    );
};

export default Cart;
